import type { SelectedImage } from '@athena/ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { decodesAsImage, screenImageFiles, type RejectedFile } from './imageSelection.js'

export interface ChosenImage extends SelectedImage {
  /** The file itself, which is what the pipeline reads. Never uploaded. */
  file: File
}

export interface ImageSelection {
  images: readonly ChosenImage[]
  /** The files turned away by the most recent choice, and why. */
  rejected: readonly RejectedFile[]
  /** Screens, decodes and appends. Keeps the order the files were given in. */
  add: (files: readonly File[]) => Promise<void>
  remove: (id: string) => void
  clear: () => void
  /**
   * Attach the corrected pages a reading produced, keyed by image id.
   *
   * Replaces whatever was there, and takes ownership of the URLs: they are
   * revoked with their image, on the next reading, and on unmount.
   */
  setProcessedUrls: (urls: ReadonlyMap<string, string>) => void
  /** Drop every corrected page. What a new reading calls before it starts. */
  revokeProcessed: () => void
}

/**
 * The images a tutor has chosen, and the object URLs their thumbnails use.
 *
 * The hook exists for the URLs. `URL.createObjectURL` pins the file in memory
 * until it is revoked, so every one created here is revoked when its image is
 * removed, when the selection is cleared, and when the screen goes away —
 * which is the kind of bookkeeping that goes wrong when it is spread across a
 * component's event handlers.
 *
 * There are two URLs per image once a reading has run: the photograph the
 * tutor chose, and the corrected page the pipeline produced from it. The
 * second is kept in its own map rather than written into the image objects,
 * so that attaching it does not rebuild the selection and a reading cannot
 * quietly reorder it. Both are revoked together.
 *
 * Nothing here talks to the server, runs OCR, or knows what OpenCV is. This
 * is the selection; `ocr.ts` and `imaging/` are what read it.
 */
export function useImageSelection(): ImageSelection {
  const [chosen, setChosen] = useState<readonly ChosenImage[]>([])
  const [rejected, setRejected] = useState<readonly RejectedFile[]>([])
  const [processed, setProcessed] = useState<ReadonlyMap<string, string>>(new Map())

  const images = useMemo<readonly ChosenImage[]>(
    () =>
      chosen.map((image) => {
        const processedUrl = processed.get(image.id)
        return processedUrl === undefined ? image : { ...image, processedUrl }
      }),
    [chosen, processed],
  )

  /**
   * Everything currently holding an object URL, for the unmount cleanup.
   *
   * Written in an effect rather than during render, and read only from the
   * cleanup, which runs after the last effect has synced it — so it holds the
   * final state rather than the one from whichever render registered the
   * cleanup.
   */
  const live = useRef<{ images: readonly ChosenImage[]; processed: ReadonlyMap<string, string> }>({
    images: [],
    processed: new Map(),
  })
  useEffect(() => {
    live.current = { images: chosen, processed }
  }, [chosen, processed])

  useEffect(
    () => () => {
      for (const image of live.current.images) URL.revokeObjectURL(image.previewUrl)
      for (const url of live.current.processed.values()) URL.revokeObjectURL(url)
    },
    [],
  )

  const revokeProcessed = useCallback(() => {
    for (const url of processed.values()) URL.revokeObjectURL(url)
    setProcessed(new Map())
  }, [processed])

  const setProcessedUrls = useCallback(
    (urls: ReadonlyMap<string, string>) => {
      for (const [id, url] of processed) {
        if (urls.get(id) !== url) URL.revokeObjectURL(url)
      }
      setProcessed(new Map(urls))
    },
    [processed],
  )

  const add = useCallback(
    async (files: readonly File[]) => {
      // Against the selection on screen: the file picker and a drop each fire
      // once, so this is the selection the tutor is looking at. The names go in
      // as well as the count, because a file already in the list is refused by
      // name — before anything decodes it.
      const screened = screenImageFiles(
        files,
        chosen.map((image) => image.name),
      )

      const accepted: ChosenImage[] = []
      const undecodable: RejectedFile[] = []
      for (const file of screened.accepted) {
        if (await decodesAsImage(file)) {
          accepted.push({
            id: crypto.randomUUID(),
            name: file.name,
            previewUrl: URL.createObjectURL(file),
            file,
          })
        } else {
          undecodable.push({ name: file.name, reason: 'unreadable' })
        }
      }

      setChosen((current) => [...current, ...accepted])
      setRejected([...screened.rejected, ...undecodable])
    },
    [chosen],
  )

  const remove = useCallback(
    (id: string) => {
      const going = chosen.find((image) => image.id === id)
      if (going) URL.revokeObjectURL(going.previewUrl)
      const processedUrl = processed.get(id)
      if (processedUrl !== undefined) {
        URL.revokeObjectURL(processedUrl)
        setProcessed((current) => {
          const next = new Map(current)
          next.delete(id)
          return next
        })
      }
      setChosen((current) => current.filter((image) => image.id !== id))
    },
    [chosen, processed],
  )

  const clear = useCallback(() => {
    for (const image of chosen) URL.revokeObjectURL(image.previewUrl)
    for (const url of processed.values()) URL.revokeObjectURL(url)
    setChosen([])
    setProcessed(new Map())
    setRejected([])
  }, [chosen, processed])

  return { images, rejected, add, remove, clear, setProcessedUrls, revokeProcessed }
}
